import React, { useRef, useEffect, forwardRef } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useParams, useNavigate } from 'react-router-dom'
// @ts-ignore
import { Tooltip, Skeleton } from '@netbasequid/core'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'

import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { useAppSelector } from 'app/hooks'
import {
  selectDisplayDashboardInfos,
  selectDashboardInfoStatus,
  selectCategoryIsTemplate,
  selectHasLabels,
  selectDashboardInfoSortingAndFilter
} from 'features/dashboardInfo/selectors'
import { DashboardInfo } from 'schemas/DashboardInfoSchema'
import { sendMessage } from 'utils/iframeUtil'
import { AutomationId } from 'utils/automationUtil'
import { Theme } from 'features/ui/types'
import { ScrollbarStyle } from 'styles'

const DashboardListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Title = styled.div<{
  $theme: SystemTheme
  $hasLabels: boolean
}>`
  font-size: ${({ $theme }) => $theme.typescale[16].size};
  line-height: ${({ $theme }) => $theme.typescale[16].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  font-family: var(--large-font-family);
  color: ${({ $theme }) => $theme.text.primary};
  margin-bottom: 8px;
  flex-shrink: 0;
  padding-left: ${({ $hasLabels }) => ($hasLabels ? '16px' : '24px')};
`
const ListContainer = styled.div<{ $mode: Theme }>`
  flex-grow: 1;
  overflow: auto;
  width: 100%;
  ${ScrollbarStyle}
`
const DashboardItemContainer = styled.div<{
  $theme: SystemTheme
  $selected: boolean
  $hasLabels: boolean
}>`
  width: 100%;
  padding: ${({ $hasLabels }) => ($hasLabels ? '9px 24px 9px 16px' : '9px 24px')};
  box-sizing: border-box;
  color: ${({ $theme }) => $theme.text.primary};
  background-color: ${({ $theme, $selected }) =>
    $selected ? $theme.interaction.onElevation.selected : 'inherit'};
  cursor: pointer;

  &:hover {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.default};
  }
  &:active {
    background-color: ${({ $theme }) => $theme.interaction.onElevation.pressed};
  }
`

const DashboardItem = styled.div<{ $theme: SystemTheme }>`
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const NoResultsContainer = styled.div<{ $theme: SystemTheme }>`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $theme }) => $theme.text.placeholder};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
`

const StyledSkeleton = styled(Skeleton)<{
  $hasLabels: boolean
}>`
  height: 22px;
  margin: ${({ $hasLabels }) => ($hasLabels ? '9px 24px 9px 16px' : '9px 24px')};
  width: unset;
  & + & {
    margin: ${({ $hasLabels }) => ($hasLabels ? '18px 24px 18px 16px' : '18px 24px')};
  }
`

const NoResults = () => {
  const theme = useSysTheme()
  const { t } = useTranslation()
  return (
    <NoResultsContainer data-testid={AutomationId.sidePannel.noResultsFound} $theme={theme}>
      {t('noResultsFound')}
    </NoResultsContainer>
  )
}

const LoadingSkeletons = () => {
  const mode = useDisplayTheme()
  const hasLabels = useAppSelector(selectHasLabels)
  return (
    <div>
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" $hasLabels={hasLabels} />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" $hasLabels={hasLabels} />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" $hasLabels={hasLabels} />
      <StyledSkeleton mode={mode} variation="rectangle" animation="wave" $hasLabels={hasLabels} />
    </div>
  )
}

const StyledText = styled.span<{
  $theme: SystemTheme
  $bold?: boolean
}>`
  color: ${({ $theme }) => $theme.text.primary};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme, $bold }) =>
    $bold ? $theme.fontWeight.semibold : $theme.fontWeight.regular};

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DisplayField = ({ label, content }: { label?: string; content?: string }) => {
  const theme = useSysTheme()

  return (
    <>
      <StyledText data-testid={AutomationId.dashboardInfo.field} $theme={theme}>
        {label}
      </StyledText>
      <StyledText data-testid={AutomationId.dashboardInfo.value} $theme={theme} $bold>
        {content}
      </StyledText>
    </>
  )
}

const DashboardInfosContainer = styled.div`
  max-width: 600px;
  padding: 8px 12px;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
`

const DashboardInfoDisplay = ({ dashboardInfo }: { dashboardInfo: DashboardInfo }) => {
  const { t } = useTranslation()
  const {
    name,
    createDatetime,
    labels,
    ownerInfo,
    liveReportEnabled,
    scheduledReportEnabled
  } = dashboardInfo
  // * add scheduled report into reportEnabled in the future
  const reportEnabled = [
    ...(liveReportEnabled ? [t('liveReport')] : []),
    ...(scheduledReportEnabled ? [t('scheduledReport')] : [])
  ]
  return (
    <DashboardInfosContainer>
      <DisplayField label={t('name')} content={name} />
      {labels && labels?.length > 0 && (
        <DisplayField label={t('label_other')} content={labels.join(', ')} />
      )}

      <DisplayField
        label={t('dateCreated')}
        content={dayjs(Number(createDatetime)).format('MMM D, YYYY')}
      />
      {reportEnabled.length > 0 && (
        <DisplayField
          label={t('funcEnabled', { func: t('report') })}
          content={
            reportEnabled.length > 1
              ? t('aAndB', { itemA: reportEnabled[0], itemB: reportEnabled[1] })
              : reportEnabled[0]
          }
        />
      )}
      <DisplayField label={t('createdBy')} content={ownerInfo.name} />
    </DashboardInfosContainer>
  )
}

const DashboardName = forwardRef(({ children }: { children?: string }, ref) => {
  const theme = useSysTheme()
  const { filter } = useAppSelector(selectDashboardInfoSortingAndFilter)

  let parts = [] as {
    text: string
    highlight: boolean
  }[]
  if (filter.keyword) {
    const matches = match(children!, filter.keyword!, { insideWords: true })
    parts = parse(children!, matches)
  }

  return (
    <DashboardItem $theme={theme} ref={ref as React.RefObject<HTMLDivElement>}>
      {parts.length === 0 ? (
        <span>{children}</span>
      ) : (
        parts.map((part, partIndex) => (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={partIndex}
            style={{ fontWeight: part.highlight ? 600 : 400 }}>
            {part.text}
          </span>
        ))
      )}
    </DashboardItem>
  )
})

const DashboardList = () => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const { t } = useTranslation()
  const { dashboardId } = useParams()
  const navigate = useNavigate()
  const targetDashboardRef = useRef<HTMLDivElement>(null)

  const dashboardInfos = useAppSelector(selectDisplayDashboardInfos)
  const hasDashboard = dashboardInfos.length > 0
  const status = useAppSelector(selectDashboardInfoStatus)
  const categoryIsTemplate = useAppSelector(selectCategoryIsTemplate)
  const hasLabels = useAppSelector(selectHasLabels)

  useEffect(() => {
    if (targetDashboardRef.current && status !== 'loading') {
      targetDashboardRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [status])

  const handleDashboardItemClick = (id: number) => {
    navigate(`/dashboard/${id}`)
    sendMessage({ type: 'v4DashboardClick', data: id })
  }

  return (
    <DashboardListContainer>
      {hasLabels && (
        <Title
          data-testid={AutomationId.sidePannel.dashboardTitle}
          $theme={theme}
          $hasLabels={hasLabels}>
          {categoryIsTemplate ? t('template_other') : t('dashboard_other')}
        </Title>
      )}
      <ListContainer $mode={mode}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {status === 'loading' ? (
          <LoadingSkeletons />
        ) : hasDashboard ? (
          dashboardInfos.map((dashboardInfo: DashboardInfo) => (
            <DashboardItemContainer
              key={dashboardInfo.id}
              $theme={theme}
              data-testid={AutomationId.sidePannel.dashboard}
              $selected={dashboardInfo.id === Number(dashboardId)}
              $hasLabels={hasLabels}
              ref={dashboardInfo.id === Number(dashboardId) ? targetDashboardRef : null}
              onClick={() => handleDashboardItemClick(dashboardInfo.id!)}>
              <Tooltip
                mode={mode}
                content={<DashboardInfoDisplay dashboardInfo={dashboardInfo} />}
                placement="right"
                offset={[0, 20]}
                followCursor={false}>
                <DashboardName>{dashboardInfo.name}</DashboardName>
              </Tooltip>
            </DashboardItemContainer>
          ))
        ) : (
          <NoResults />
        )}
      </ListContainer>
    </DashboardListContainer>
  )
}

export default DashboardList
